import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import { VALEUR_PAR_FAMILLE_DATA, ValeurGroupBy } from "../../models/data.model";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { Chart } from "chart.js";
import { SharedModule } from "../../../../shared/shared.module";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { ValeurParFamilleDialogComponent } from "./valeur-par-famille-dialog.component";

@Component({
  selector: "vl-widget-valeur-par-famille",
  standalone: true,
  imports: [SharedModule, WidgetTitleComponent, ChipsToggleComponent, ChipsComponent],
  host: { class: "widget-component" },
  templateUrl: "./widget-valeur-par-famille.component.html",
  styleUrl: "./widget-valeur-par-famille.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetValeurParFamilleComponent implements OnDestroy {
  data = input<VALEUR_PAR_FAMILLE_DATA>();
  metadata = input<EntityMetadata>();
  maxItems = 8;
  canvasRef = viewChild<ElementRef>("repart");
  widget = input<Widget>();
  chart: any;
  labels = signal(null);

  private dialogsService = inject(DialogsService);

  groupBy = signal<ValeurGroupBy>("byFamille");
  groupByElements = [
    { name: "Famille", value: "byFamille" as ValeurGroupBy },
    { name: "Article", value: "byArticle" as ValeurGroupBy },
    { name: "Entrepôt", value: "byEntrepot" as ValeurGroupBy },
  ];

  private groupByLabels: Record<ValeurGroupBy, string> = {
    byFamille: "Valeur stock — Famille",
    bySousFamille: "Valeur stock — Sous-famille",
    byArticle: "Valeur stock — Article",
    byEntrepot: "Valeur stock — Entrepôt",
  };

  activeData = computed(() => {
    const d = this.data();
    if (!d) return null;
    return d[this.groupBy()]?.filter((item) => item.total > 0) ?? null;
  });

  valeurTotal = computed(() => {
    if (this.data()) {
      return WidgetHelperService.formatNumeric(this.data()?.total) + " €";
    } else {
      return "- €";
    }
  });

  constructor() {
    effect(
      () => {
        const items = this.activeData();
        if (items?.length && this.canvasRef()) {
          this.buildChart(items);
        }
      },
      { allowSignalWrites: true },
    );
  }

  buildChart(items: { denomination: string; total: number }[]) {
    const chartData = WidgetHelperService.getValeurParFamilleChart(
      items,
      this.maxItems,
    );

    if (!chartData.labels.length) {
      return;
    }

    const canvas = this.canvasRef().nativeElement;
    const ctx = canvas.getContext("2d");
    if (this.chart) {
      this.chart.destroy();
    }
    this.chart = new Chart(ctx, {
      type: "doughnut",
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: false,
          },
        },
      },
    });

    this.computeLabels(chartData);
  }

  private computeLabels(chartData: { labels: string[]; datasets: any[] }) {
    const values = chartData.datasets[0].data;
    const total = values.reduce((a, b) => +a + +b, 0);
    const colors = chartData.datasets[0].backgroundColor ?? [];

    this.labels.set(
      chartData.labels.map((label, i) => ({
        label,
        percentage: ((+values[i] / total) * 100).toFixed(0),
        color: colors[i],
      })),
    );
  }

  openFullList() {
    const items = this.activeData();
    if (!items?.length) return;

    const colorMap: Record<string, string> = {};
    const labels = this.labels();
    if (labels) {
      for (const l of labels) {
        colorMap[l.label] = l.color;
      }
    }

    this.dialogsService.open(
      ValeurParFamilleDialogComponent,
      "min(95vw, 860px)",
      "fit-content",
      {
        title: this.groupByLabels[this.groupBy()],
        items,
        details: this.data()?.details ?? [],
        total: this.data()?.total ?? 0,
        groupBy: this.groupBy(),
        colorMap,
        fullData: this.data(),
      },
    );
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
